import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  Box,
  Chip,
  Link,
  Typography,
} from '@mui/material';
import { useDialog } from 'hooks';
import { originalDays } from 'utilities';
import dayjs from 'dayjs';

// Locale-safe: derive lowercase day key from JS Date getDay()
// getDay() returns 0=Sunday...6=Saturday; originalDays is Mon-first
const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const getDayKey = () => {
  const jsDay = new Date().getDay(); // 0=Sun
  return DAYS[jsDay === 0 ? 6 : jsDay - 1];
};

export function OpenHoursTypography({ restaurant }) {
  const { select } = useDialog();
  const today = new Date();
  const now = new Date();
  const dayName = getDayKey();
  const todayHours = restaurant?.operatingHours?.[dayName];

  const formatTime = (hourStr) => {
    const [h, m = 0] = hourStr.split(':').map(Number);
    return dayjs().hour(h).minute(m).format('h:mm A');
  };

  let isOpen = false;

  if (todayHours?.open && todayHours?.close) {
    const [openHour, openMinute = '0'] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMinute = '0'] = todayHours.close.split(':').map(Number);

    const openTime = new Date(today);
    openTime.setHours(openHour, openMinute, 0, 0);

    const closeTime = new Date(today);
    closeTime.setHours(closeHour, closeMinute, 0, 0);

    if (closeTime <= openTime) {
      closeTime.setDate(closeTime.getDate() + 1);
    }

    isOpen = now >= openTime && now <= closeTime;
  }

  const getOpenStatus = () => {
    if (isOpen) {
      return `· Closes ${formatTime(todayHours.close)}`;
    }

    if (todayHours?.open) {
      const [openHour, openMinute = '0'] = todayHours.open.split(':').map(Number);
      const openTime = new Date(today);
      openTime.setHours(openHour, openMinute, 0, 0);

      if (now < openTime) {
        return `· Opens ${formatTime(todayHours.open)}`;
      }
    }

    for (let i = 1; i <= 7; i++) {
      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + i);
      const nextDayName = dayjs(nextDate).format('dddd').toLowerCase();
      const nextHours = restaurant.operatingHours?.[nextDayName];

      if (nextHours?.open && nextHours?.close) {
        const dayLabel = i === 1 ? 'tomorrow' : dayjs().add(i, 'day').format('dddd');
        return `· Opens ${formatTime(nextHours.open)} ${dayLabel}`;
      }
    }

    return '';
  };

  const handleShowHours = async () => {
    const sortedHours = originalDays.map((dayLabel) => {
      const key = dayLabel.toLowerCase();
      const hours = restaurant.operatingHours?.[key];
      return {
        label: `${dayLabel}: ${hours?.open && hours?.close
          ? `${formatTime(hours.open)} – ${formatTime(hours.close)}`
          : 'Closed'}`,
        value: dayLabel,
      };
    });

    const title = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6">Opening Hours</Typography>
        <Chip
          label={isOpen ? 'Open' : 'Closed'}
          size="small"
          sx={{
            bgcolor: isOpen ? 'success.main' : 'error.main',
            color: 'white',
            fontWeight: 500,
          }}
        />
      </Box>
    );

    await select({
      title,
      options: sortedHours,
      selectedValue: DAYS.indexOf(dayName) >= 0
        ? originalDays[DAYS.indexOf(dayName)]
        : originalDays[0],
      disableInteraction: true,
      allowClickAwayClose: true
    });
  };

  return (
    <Link
      href="#"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleShowHours();
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      <Typography
        component="span"
        variant="body2"
        color="text.secondary"
        sx={{ display: 'inline-flex', alignItems: 'center', mb: 0.5 }}
      >
        <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
        <Box
          component="span"
          sx={{ color: isOpen ? 'success.main' : 'error.main', fontWeight: 500, mr: 0.5 }}
        >
          {isOpen ? 'Open' : 'Closed'}
        </Box>
        {getOpenStatus()}
      </Typography>
    </Link>
  );
}